/**
 * Shared quaternion math utilities for skeletal animation.
 *
 * Provides quaternion distance (for PSD corrective weights), swing-twist
 * decomposition, and angle normalization used by the constraint system
 * and joint angle measurement.
 */

import * as THREE from "three/webgpu";
import { logAntiParallel } from "./ikDebugLog.ts";

// Scratch objects — reused across calls to avoid per-frame allocations
const _tmpInv = new THREE.Quaternion();
const _extractSwing = new THREE.Quaternion();
const _extractTwist = new THREE.Quaternion();
const _dtlOrigDir = new THREE.Vector3();
const _dtlNewDir = new THREE.Vector3();
const _dtlCrossAxis = new THREE.Vector3();
const _dtlQ1 = new THREE.Quaternion();
const _dtlQ2 = new THREE.Quaternion();
const _dtlQ3 = new THREE.Quaternion();

/** Normalize an angle to the range [-PI, PI]. */
export function normalizeAngle(angle: number): number {
  angle %= 2 * Math.PI;
  if (angle > Math.PI) angle -= 2 * Math.PI;
  else if (angle < -Math.PI) angle += 2 * Math.PI;
  return angle;
}

/**
 * Decompose a quaternion into swing and twist components around a given axis.
 * twist rotates around the axis; swing rotates the axis to its new orientation.
 * q = swing * twist
 */
export function decomposeSwingTwist(
  q: THREE.Quaternion,
  axis: THREE.Vector3,
  outSwing: THREE.Quaternion,
  outTwist: THREE.Quaternion,
): void {
  // Project the quaternion's vector part onto the twist axis
  const dot = q.x * axis.x + q.y * axis.y + q.z * axis.z;

  outTwist.set(axis.x * dot, axis.y * dot, axis.z * dot, q.w);
  const twistLen = outTwist.length();
  if (twistLen < 1e-10) {
    // No twist component — twist is identity
    outTwist.set(0, 0, 0, 1);
    outSwing.copy(q);
  } else {
    const invLen = 1 / twistLen;
    outTwist.set(
      outTwist.x * invLen,
      outTwist.y * invLen,
      outTwist.z * invLen,
      outTwist.w * invLen,
    );
    // swing = q * inverse(twist)
    _tmpInv.copy(outTwist).invert();
    outSwing.copy(q).multiply(_tmpInv);
  }
}

/**
 * Extract the signed rotation angle around a specific axis from a quaternion.
 * Uses swing-twist decomposition to isolate the twist (rotation around axis),
 * then computes the signed angle.
 *
 * @returns Signed angle in radians, normalized to [-PI, PI]
 */
export function extractAxisAngle(
  q: THREE.Quaternion,
  axis: THREE.Vector3,
): number {
  decomposeSwingTwist(q, axis, _extractSwing, _extractTwist);

  const raw =
    2 *
    Math.atan2(
      _extractTwist.x * axis.x +
        _extractTwist.y * axis.y +
        _extractTwist.z * axis.z,
      _extractTwist.w,
    );
  return normalizeAngle(raw);
}

// ---------------------------------------------------------------------------
// Pose-space deformation (PSD) utilities
// ---------------------------------------------------------------------------

/** Quaternion tuple [x, y, z, w] as stored in corrective bindings. */
export type Quat4 = [number, number, number, number];

/** Hamilton product of two quaternions (x, y, z, w). */
export function qmul(a: Quat4, b: Quat4): Quat4 {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/** Inverse (conjugate) of a unit quaternion. */
export function qinv(q: Quat4): Quat4 {
  return [-q[0], -q[1], -q[2], q[3]];
}

/**
 * Rotation angle between two quaternions, in radians.
 *
 * Returns the unsigned geodesic distance on the unit quaternion sphere,
 * which equals the rotation angle to get from one orientation to the other.
 * Range: [0, π].
 *
 * @param ax - First quaternion x component
 * @param ay - First quaternion y component
 * @param az - First quaternion z component
 * @param aw - First quaternion w component
 * @param bx - Second quaternion x component
 * @param by - Second quaternion y component
 * @param bz - Second quaternion z component
 * @param bw - Second quaternion w component
 */
export function quaternionAngle(
  ax: number,
  ay: number,
  az: number,
  aw: number,
  bx: number,
  by: number,
  bz: number,
  bw: number,
): number {
  // |dot| accounts for double-cover: q and -q are the same rotation
  const dot = Math.abs(ax * bx + ay * by + az * bz + aw * bw);
  // Clamp for numerical safety, then convert to rotation angle
  return 2 * Math.acos(Math.min(1, dot));
}

// ---------------------------------------------------------------------------
// Shared world-to-local quaternion conversion for IK solvers
// ---------------------------------------------------------------------------

/**
 * Convert solved bone positions to local quaternions using iterative parent
 * propagation.
 *
 * For each bone, computes the world-space rotation from the original direction
 * to the solved direction, then converts to a local quaternion relative to the
 * running parent world quaternion. Each bone's local quat is computed using
 * the *updated* parent world quaternion (not the stale captured one), so error
 * doesn't compound down the chain.
 *
 * Handles near-antiparallel directions (dot ≈ -1) explicitly to avoid the
 * discontinuity in THREE.Quaternion.setFromUnitVectors.
 *
 * @param origPositions  Original joint positions (length = bones + 1 for tip)
 * @param solvedPositions Solved joint positions (same length)
 * @param boneNames      Bone names in order
 * @param startLocalQuats Local quaternions at solve start
 * @param parentWorldQuats Parent world quaternions at solve start
 * @param firstParentWorld The first bone's parent world quaternion (chain root's parent)
 * @returns Map of boneName → quaternion [x,y,z,w]
 */
export function directionsToLocalQuaternions(
  origPositions: THREE.Vector3[],
  solvedPositions: THREE.Vector3[],
  boneNames: string[],
  startLocalQuats: THREE.Quaternion[],
  parentWorldQuats: THREE.Quaternion[],
  firstParentWorld: THREE.Quaternion,
): Map<string, Quat4> {
  const result = new Map<string, Quat4>();

  // Running parent world quaternion, updated as we compute each bone.
  const runningParentWorld = firstParentWorld.clone();

  for (let i = 0; i < boneNames.length; i++) {
    // Original bone direction
    _dtlOrigDir.subVectors(origPositions[i + 1], origPositions[i]);
    const origLen = _dtlOrigDir.length();

    // Solved bone direction
    _dtlNewDir.subVectors(solvedPositions[i + 1], solvedPositions[i]);
    const newLen = _dtlNewDir.length();

    // Guard against zero-length bones (degenerate / collapsed joints)
    let worldDelta: THREE.Quaternion;
    if (origLen < 1e-10 || newLen < 1e-10) {
      worldDelta = _dtlQ1.identity();
    } else {
      _dtlOrigDir.multiplyScalar(1 / origLen);
      _dtlNewDir.multiplyScalar(1 / newLen);

      // setFromUnitVectors is unstable when vectors are nearly anti-parallel
      const dot = _dtlOrigDir.dot(_dtlNewDir);
      if (dot < -0.9999) {
        logAntiParallel(boneNames[i], dot);
        _dtlCrossAxis.set(1, 0, 0);
        if (Math.abs(_dtlOrigDir.dot(_dtlCrossAxis)) > 0.9) {
          _dtlCrossAxis.set(0, 1, 0);
        }
        _dtlCrossAxis.crossVectors(_dtlOrigDir, _dtlCrossAxis).normalize();
        worldDelta = _dtlQ1.setFromAxisAngle(_dtlCrossAxis, Math.PI);
      } else {
        worldDelta = _dtlQ1.setFromUnitVectors(_dtlOrigDir, _dtlNewDir);
      }
    }

    // newLocal = inv(runningParentWorld) * worldDelta * origParentWorld * origLocal
    _dtlQ2.copy(runningParentWorld).invert();
    _dtlQ3
      .copy(_dtlQ2)
      .multiply(worldDelta)
      .multiply(parentWorldQuats[i])
      .multiply(startLocalQuats[i]);
    _dtlQ3.normalize();

    result.set(boneNames[i], [_dtlQ3.x, _dtlQ3.y, _dtlQ3.z, _dtlQ3.w]);

    // Update running parent: this bone's new world quat = runningParentWorld * newLocal
    runningParentWorld.multiply(_dtlQ3);
  }

  return result;
}

/**
 * Mirror a quaternion for bilateral bone symmetry (left → right).
 *
 * For a standard humanoid rig, the right-side equivalent of a left-side
 * rotation has its Y and Z components negated:
 *   (x, y, z, w) → (x, −y, −z, w)
 *
 * This replaces the old per-axis Z-flip logic with a single general operation
 * that works for any rotation, including diagonal (multi-axis) poses.
 */
export function mirrorQuaternion(q: Quat4): Quat4 {
  return [q[0], -q[1], -q[2], q[3]];
}
